import { useState } from 'react';
import { useAssets, ASSET_CATEGORY_LABELS, ASSET_STATUS_LABELS, ASSET_STATUS_COLORS, AssetCategory, AssetStatus, ShopAsset } from '@/hooks/useAssets';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Plus, Package, Wrench, AlertTriangle, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface AssetListProps {
  selectedAssetId: string | null;
  onSelectAsset: (assetId: string) => void;
  onCreateAsset: () => void;
}

export function AssetList({ selectedAssetId, onSelectAsset, onCreateAsset }: AssetListProps) {
  const { data: assets, isLoading } = useAssets();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<AssetCategory | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<AssetStatus | 'all'>('all');

  const filteredAssets = (assets ?? []).filter((asset) => {
    const matchesSearch = 
      asset.name.toLowerCase().includes(search.toLowerCase()) ||
      asset.manufacturer?.toLowerCase().includes(search.toLowerCase()) ||
      asset.model?.toLowerCase().includes(search.toLowerCase()) ||
      asset.serial?.toLowerCase().includes(search.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || asset.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || asset.status === statusFilter;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Group assets by category for display
  const groupedAssets = filteredAssets.reduce((acc, asset) => {
    const category = asset.category;
    if (!acc[category]) acc[category] = [];
    acc[category].push(asset);
    return acc;
  }, {} as Record<AssetCategory, ShopAsset[]>);

  // Display order across all 19 DB-CHECK-allowed AssetCategory values.
  // Bay categories first (auto-created by ensure_bay_has_asset trigger),
  // then equipment grouped by family, then catch-alls last.
  const categoryOrder: AssetCategory[] = [
    // Bay-shape (auto-created)
    'lift_bay', 'flat_bay',
    // Scheduling-relevant equipment
    'alignment', 'ac_machine', 'tire_machine', 'wheel_balancer',
    // Diagnostic
    'scan_tool', 'diagnostic_equipment', 'adas',
    // Service / fluid / power
    'fluid_service_machine', 'battery_equipment', 'compressor',
    // Fabrication
    'welder', 'press',
    // Tooling / catch-alls
    'specialty_tooling', 'shop_equipment',
    // IT / software
    'computer_equipment', 'software_subscription',
    // Fallback
    'other',
  ];

  // Count assets needing attention
  const attentionCount = (assets ?? []).filter(
    a => a.status === 'out_of_service' || a.status === 'awaiting_parts'
  ).length;

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="p-4 border-b space-y-3">
          <Skeleton className="h-10 w-full" />
          <div className="flex gap-2">
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-9 w-28" />
          </div>
        </div>
        <div className="flex-1 p-4 space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search assets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button onClick={onCreateAsset} size="icon">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v as AssetCategory | 'all')}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categoryOrder.map(cat => (
                <SelectItem key={cat} value={cat}>{ASSET_CATEGORY_LABELS[cat]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as AssetStatus | 'all')}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.entries(ASSET_STATUS_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {attentionCount > 0 && (
          <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4" />
            <span>{attentionCount} asset{attentionCount > 1 ? 's' : ''} need attention</span>
          </div>
        )}
      </div>

      {/* Asset List */}
      <div className="flex-1 overflow-y-auto">
        {filteredAssets.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {search || categoryFilter !== 'all' || statusFilter !== 'all'
                ? 'No assets match your filters'
                : 'No assets yet'}
            </p>
            {!search && categoryFilter === 'all' && statusFilter === 'all' && (
              <Button variant="outline" className="mt-4" onClick={onCreateAsset}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Asset
              </Button>
            )}
          </div>
        ) : (
          <div className="divide-y">
            {categoryOrder.map(category => {
              const categoryAssets = groupedAssets[category];
              if (!categoryAssets || categoryAssets.length === 0) return null;
              
              return (
                <div key={category}>
                  <div className="px-4 py-2 bg-muted/50 text-sm font-medium text-muted-foreground sticky top-0">
                    {ASSET_CATEGORY_LABELS[category]} ({categoryAssets.length})
                  </div>
                  {categoryAssets.map(asset => (
                    <button
                      key={asset.id}
                      onClick={() => onSelectAsset(asset.id)}
                      className={cn(
                        'w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors',
                        'flex items-start gap-3',
                        selectedAssetId === asset.id && 'bg-muted'
                      )}
                    >
                      <div className={cn(
                        'flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center',
                        asset.status === 'out_of_service' && 'ring-2 ring-destructive/50'
                      )}>
                        <Wrench className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{asset.name}</span>
                          {asset.status === 'out_of_service' && (
                            <Badge variant="destructive" className="text-xs">Down</Badge>
                          )}
                          <Badge
                            variant="secondary"
                            className={cn('text-xs', ASSET_STATUS_COLORS[asset.status])}
                          >
                            {ASSET_STATUS_LABELS[asset.status]}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground truncate">
                          {[asset.manufacturer, asset.model].filter(Boolean).join(' ')
                            || asset.asset_type
                            || 'No details'}
                        </div>
                        {asset.home_bay && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                            <MapPin className="h-3 w-3" />
                            {asset.home_bay.name}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
